import React, { useState, useEffect, lazy, Suspense } from "react";
import { Icon } from "@iconify/react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { get } from "./APICRUD/apiClient";
import { logger } from "./utils/logger";
import { ErrorBoundary } from "./Components/ErrorBoundary";

// Toast provider for error handling
import { ToastProvider } from "./context/ToastContext";

// Auth imports - OAuth mode
import { AuthProvider, useAuth } from "./context/AuthContext";
import {
  Login,
  AuthCallback,
  ProtectedRoute,
  AdminProtectedRoute,
  UserProfile,
} from "./Components/Auth";

// Legacy auth - PasswordGate mode
import { PasswordGate } from "./CommonComponents/PasswordGate";


// Check if OAuth is enabled (default: true)
const useOAuth = process.env.REACT_APP_USE_OAUTH !== "false";

/**
 * Get environment label based on hostname
 * Production = no label (hostname contains "qualityplatform" but NOT "-qa")
 * QA = "QAS" (hostname contains "qualityplatform-qa" or starts with "qa.")
 * Localhost or anything else = "DEV"
 *
 * Works with both subdomain (qualityplatform.tilli.pro) and
 * dedicated TLD (qualityplatform.pro) patterns
 */
const getEnvLabel = () => {
  const host = window.location.hostname.toLowerCase();
  // Check QA patterns: "qualityplatform-qa" or "qa.qualityplatform"
  if (host.includes("qualityplatform-qa") || host.startsWith("qa.")) return "QAS";
  // Production: contains "qualityplatform" (without -qa suffix already handled above)
  if (host.includes("qualityplatform")) return null;
  return "DEV"; // localhost or any other environment
};

// Lazy load route components for better performance
const Public = lazy(() =>
  import("./Components/Public/PublicDashboard").then((module) => ({
    default: module.PublicDashboard,
  }))
);
const ProjectInsight = lazy(() =>
  import("./Components/ProjectInsights/ProjectInsight").then((module) => ({
    default: module.ProjectInsight,
  }))
);
const TestCase = lazy(() =>
  import("./Components/TestCases/TestCase").then((module) => ({
    default: module.TestCase,
  }))
);
const TestRun = lazy(() =>
  import("./Components/TestRuns/TestRun").then((module) => ({
    default: module.TestRun,
  }))
);
const TestPlan = lazy(() =>
  import("./Components/TestPlans/TestPlan").then((module) => ({
    default: module.TestPlan,
  }))
);
const Projects = lazy(() =>
  import("./Components/Projects/Projects").then((module) => ({
    default: module.Projects,
  }))
);
const Admin = lazy(() =>
  import("./Components/Admin/Admin").then((module) => ({
    default: module.Admin,
  }))
);

const GlobalAnalytics = lazy(() =>

  import("./Components/Analytics/GlobalAnalytics").then((module) => ({

    default: module.default,

  }))
);

const ProjectCompare = lazy(() =>
  import("./Components/Analytics/ProjectCompare").then((module) => ({
    default: module.default,
  }))
);

const menuItems = [
  {
    name: "Projects",
    icon: "tabler:folders",
    path: "/projects",
    children: [
      { name: "Test Cases", icon: "tabler:list-check", path: "/test-cases" },
      { name: "Insights", icon: "tabler:chart-line", path: "/project-insights" },
    ],
  },
  { name: "Test Runs", icon: "tabler:player-play", path: "/test-runs" },
  // { name: "Test Plans", icon: "tabler:file-description", path: "/test-plans"},
  { name: "Analytics", icon: "mdi:chart-line", path: "/analytics" },
  // Future: Add more parent items here
  // { name: "Settings", icon: "tabler:settings", path: "/settings", children: [...] }

];

/**
 * Loading Spinner Component
 */
const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-full">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);

/**
 * Header Component - Shows logo, sidebar toggle, and user profile
 */
export const Header = ({ collapsed, setCollapsed }) => {
  return (
    <div
      className="h-16 flex items-center shadow-md relative"
      style={{ background: 'linear-gradient(90deg, #4169E1 0%, #00BFFF 100%)' }}
    >
      {/* Left: Custom sidebar toggle with animated hamburger lines */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute left-4 p-2 hover:bg-white/10 rounded-md transition-colors"
        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        <div
          className={`flex items-center transition-all duration-300 ${collapsed ? "gap-0" : "gap-0.5"
            }`}
        >
          {/* Sidebar bar indicator */}
          <div className="w-1 h-5 bg-white rounded-sm" />
          {/* Hamburger lines (expanded) or 3 dots icon (collapsed) */}
          {collapsed ? (
            <Icon
              icon="tabler:dots-vertical"
              width="16"
              className="text-white -ml-1"
            />
          ) : (
            <div className="flex flex-col gap-1">
              <div className="w-4 h-0.5 bg-white rounded-full" />
              <div className="w-4 h-0.5 bg-white rounded-full" />
              <div className="w-4 h-0.5 bg-white rounded-full" />
            </div>
          )}
        </div>
      </button>

      {/* Center: Logo */}
      <div className="flex items-center gap-1 mx-auto">
        <img src="/tilli-logo.png" alt="Tilli Software" className="h-8" />
        <span
          className="text-2xl text-slate-900 lowercase tracking-tight pb-1.5"
          style={{ fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
        >
          quality platform
        </span>
        {getEnvLabel() && (
          <span
            className="ml-2 px-2 py-0.5 text-sm font-bold rounded mb-1.5"
            style={{
              backgroundColor: "#FF6B00",
              color: "#FFFFFF",
              fontFamily: "Poppins, sans-serif",
            }}
          >
            {getEnvLabel()}
          </span>
        )}
      </div>

      {/* Right: UserProfile */}
      {useOAuth && (
        <div className="absolute right-6 flex items-center gap-3">
          <UserProfile />
        </div>
      )}
    </div>
  );
};

/**
 * Sidebar Component - Navigation menu
 */
const Sidebar = ({ collapsed, setCollapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Check if a route is active (handles nested routes like /test-cases/*)
  const isActiveRoute = (path) => {
    if (path === "/projects") {
      return location.pathname === "/projects" || location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div
      className={`${collapsed ? "w-16" : "w-64"
        } bg-gray-100 border-r transition-all duration-300 flex flex-col h-full`}
    >
      {/* Menu */}
      <ul className="flex-1 overflow-auto py-4">
        {menuItems.map((item) => (
          <React.Fragment key={item.name}>
            {/* Parent item */}
            <li
              onClick={() => navigate(item.path)}
              title={collapsed ? item.name : ""}
              className={`flex items-center gap-3 cursor-pointer p-3 mx-2 my-1 rounded-md transition-colors
                ${isActiveRoute(item.path)
                  ? "bg-blue-600 text-white hover:bg-blue-700"
                  : "text-gray-700 hover:bg-gray-200"
                }`}
            >
              <Icon icon={item.icon} width="20" />
              {!collapsed && <span className="font-medium">{item.name}</span>}
            </li>

            {/* Child items (if any) */}
            {item.children?.map((child, index) => (
              <li
                key={child.name}
                onClick={() => navigate(child.path)}
                title={collapsed ? child.name : ""}
                className={`flex items-center cursor-pointer p-3 rounded-md transition-colors
                  ${collapsed ? "ml-1 mr-2 my-1" : "ml-4 mr-2 my-0.5"}
                  ${isActiveRoute(child.path)
                    ? "bg-blue-600 text-white hover:bg-blue-700"
                    : "text-gray-700 hover:bg-gray-200"
                  }`}
              >
                {/* L-shaped tree branch indicator */}
                <span className={`flex-shrink-0 ${isActiveRoute(child.path) ? "text-blue-200" : "text-gray-400"}`}>
                  <Icon icon="tabler:corner-down-right" width={collapsed ? "12" : "16"} />
                </span>
                <Icon icon={child.icon} width="20" className={collapsed ? "ml-0.5" : "ml-2"} />
                {!collapsed && (
                  <span className="font-medium ml-2">{child.name}</span>
                )}
              </li>
            ))}
          </React.Fragment>
        ))}
      </ul>
    </div>
  );
};

/**
 * Main App Layout - Header + Sidebar + Content
 */
const AppLayout = () => {
  // Auto-collapse sidebar on smaller screens (1080p and below)
  const [collapsed, setCollapsed] = useState(() => {
    return window.innerWidth <= 1920;
  });
  const [backendStatus, setBackendStatus] = useState("checking"); // 'checking' | 'connected' | 'error'

  useEffect(() => {
    get("/api/health")
      .then(() => {
        setBackendStatus("connected");
        logger.log("Backend connected successfully!");
      })
      .catch((err) => {
        setBackendStatus("error");
        logger.error("Backend connection failed:", err);
      });
  }, []);

  // Update sidebar state on window resize
  useEffect(() => {
    const handleResize = () => {
      const shouldCollapse = window.innerWidth <= 1920;
      setCollapsed(shouldCollapse);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  return (
    <div className="flex flex-col h-screen">
      {/* Backend Error Banner */}
      {backendStatus === "error" && (
        <div className="bg-red-500 text-white px-4 py-2 text-center text-sm font-medium">
          Unable to connect to server. Some features may not work.
        </div>
      )}

      {/* Header - Full Width */}
      <Header collapsed={collapsed} setCollapsed={setCollapsed} />

      {/* Main Layout: Sidebar + Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />

        {/* Right Panel with Routes */}
        <div className="flex-1 overflow-hidden">
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route path="/projects" element={<Projects />} />
              <Route path="/public/share/:token" element={<Public />} />
              <Route path="/project-insights/*" element={<ProjectInsight />} />
              <Route path="/test-cases/*" element={<TestCase />} />
              <Route path="/test-runs" element={<TestRun />} />
              <Route path="/test-plans" element={<TestPlan />} />
              <Route path="/analytics/project-compare" element={<ProjectCompare />} />
              <Route path="/analytics/*" element={<GlobalAnalytics />} />
              <Route
                path="/admin/*"
                element={
                  <AdminProtectedRoute>
                    <Admin />
                  </AdminProtectedRoute>
                }
              />
              <Route path="*" element={<Projects />} />
            </Routes>
          </Suspense>
        </div>
      </div>
    </div>
  );
};

/**
 * OAuth App - Uses AuthProvider and protected routes
 */
const OAuthApp = () => {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public routes - no authentication required */}
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/public/share/:token" element={<Public />} />

          {/* Protected routes - authentication required */}
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <ErrorBoundary>
                  <AppLayout />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Router>
    </AuthProvider>
  );
};

/**
 * Legacy App - Uses PasswordGate for simple password protection
 */
const LegacyApp = () => {
  return (
    <PasswordGate>
      <ErrorBoundary>
        <Router>
          <AppLayout />
        </Router>
      </ErrorBoundary>
    </PasswordGate>
  );
};

/**
 * Main App Export
 * Switches between OAuth and Legacy mode based on REACT_APP_USE_OAUTH env var
 */
export const App = () => {
  return (
    <ToastProvider>{useOAuth ? <OAuthApp /> : <LegacyApp />}</ToastProvider>
  );
};
